import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { PageHeader, EmptyState } from "../components/shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { BulkUploadButton } from "../components/BulkUploadButton";

const EMPTY = { name: "", type: "customer", email: "", phone: "", address: "", bank_details: "" };

export default function Contacts() {
  const [tab, setTab] = useState("customer");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => setItems((await api.get("/contacts")).data), []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/contacts/${editing}`, form);
      else await api.post("/contacts", form);
      toast.success("Saved"); setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Delete contact?")) return; await api.delete(`/contacts/${id}`); toast.success("Deleted"); load(); };

  const filtered = items.filter((c) => c.type === tab);

  return (
    <div data-testid="contacts-page">
      <PageHeader title="Contacts" subtitle="Customers & suppliers"
        action={<div className="flex gap-2">
          <BulkUploadButton uploadUrl={`/contacts/bulk?type=${tab}`} templateUrl="/contacts/template.csv"
            title={`Import ${tab === "customer" ? "Customers" : "Suppliers"}`}
            description="Upload a .csv or .xlsx with columns: name, email, phone, address."
            onDone={load} testId="contacts-import" />
          <Button className="rounded-none gap-2" data-testid="create-contact-btn"
            onClick={() => { setForm({ ...EMPTY, type: tab }); setEditing(null); setOpen(true); }}>
            <Plus className="w-4 h-4" /> New {tab === "customer" ? "Customer" : "Supplier"}
          </Button>
        </div>} />

      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList className="rounded-none">
          <TabsTrigger value="customer" className="rounded-none" data-testid="tab-customers">Customers</TabsTrigger>
          <TabsTrigger value="supplier" className="rounded-none" data-testid="tab-suppliers">Suppliers</TabsTrigger>
        </TabsList>
      </Tabs>

      {filtered.length === 0 ? <EmptyState title={`No ${tab}s yet`} /> : (
        <div className="border border-border bg-card overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-secondary">
              <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Phone</TableHead>
              <TableHead>Address</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map((c) => (
                <TableRow key={c.id} className="hover:bg-secondary/60" data-testid="contact-row">
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{c.email || "—"}</TableCell>
                  <TableCell className="font-mono text-sm">{c.phone || "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{c.address || "—"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" className="h-8 w-8 rounded-none" onClick={() => { setForm(c); setEditing(c.id); setOpen(true); }}><Pencil className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 rounded-none text-destructive" onClick={() => del(c.id)}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit" : "New"} Contact</DialogTitle><DialogDescription>Add or edit a customer or supplier.</DialogDescription></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2"><Label>Name</Label><Input className="rounded-none mt-1" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required data-testid="contact-name-input" /></div>
              <div><Label>Email</Label><Input className="rounded-none mt-1" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} /></div>
              <div><Label>Phone</Label><Input className="rounded-none mt-1" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} /></div>
              <div className="sm:col-span-2"><Label>Address</Label><Input className="rounded-none mt-1" value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} /></div>
              <div className="sm:col-span-2"><Label>Bank details <span className="text-muted-foreground">(optional)</span></Label><Input className="rounded-none mt-1" value={form.bank_details || ""} onChange={(e) => setForm((f) => ({ ...f, bank_details: e.target.value }))} placeholder="Bank · Account no. · Account name" data-testid="contact-bank-input" /></div>
            </div>
            <DialogFooter><Button type="submit" className="rounded-none" data-testid="save-contact-btn">Save</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
